<template>
  <div class="radio-buttons">
    <div
      v-for="(item, index) in visibleItems"
      :key="`radio-item-${index}`"
      class="radio-button"
      :class="{
        'radio-button--active': isActive(index),
        'radio-button--divided': divided,
        'radio-button--bordered': bordered,
        'radio-button--reversed': reversed,
      }"
      @click="setActiveItem(index)"
    >
      <slot :name="`item-${index}`" :item="item" :isActive="isActive(index)">
        <div
          class="radio-button__text"
          :class="{
            'radio-button__text--large': large,
          }"
        >
          {{ items[item]?.text || item }}
        </div>
        <div class="radio-button__icon">
          <div
            :class="{
              'i-checkbox-filled': isActive(index),
              'i-checkbox-empty': !isActive(index),
            }"
          ></div>
        </div>
      </slot>
    </div>
    <slot
      v-if="isExpandable"
      name="expander"
      :expand="toggleHiddenState"
      :isHidden="!isExpanded"
    ></slot>
  </div>
</template>

<script>
import { shallowRef } from "@vue/reactivity";

export default {
  props: {
    modelValue: {
      type: [Number, String],
      requried: true,
    },
    divided: {
      type: Boolean,
      default: false,
    },
    bordered: {
      type: Boolean,
      default: false,
    },
    reversed: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    items: {
      type: [Array, Object],
      required: true,
    },
    visibleCount: {
      type: [Number, null],
      default: null,
    },
  },
  emits: ["update:modelValue", "selected"],
  setup(props, { emit }) {
    const isArrayItems = Array.isArray(props.items);
    const items = isArrayItems ? props.items : Object.keys(props.items);
    const slicedItems = items.slice(0, props.visibleCount || items.length);
    const isExpanded = shallowRef(false);
    const isExpandable = shallowRef(items.length - slicedItems.length > 1);
    const visibleItems = shallowRef(isExpandable.value ? slicedItems : items);

    function setActiveItem(i) {
      const val = isArrayItems ? i : items[i];
      emit("selected", val);
      emit("update:modelValue", val);
    }

    function isActive(i) {
      const val = isArrayItems ? i : items[i];
      return props.modelValue === val;
    }

    function toggleHiddenState() {
      isExpanded.value = !isExpanded.value;
      visibleItems.value = isExpanded.value ? items : slicedItems;
    }

    return {
      isExpanded,
      isExpandable,
      visibleItems,
      toggleHiddenState,
      setActiveItem,
      isActive,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

.radio-buttons {
  font-family: Roboto-Regular;
  background-color: white;

  &__additional {
    overflow: hidden;
  }

  .radio-button {
    padding: 15px;
    height: 65px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 2px;
    overflow: hidden;

    &--reversed {
      flex-direction: row-reverse;

      .radio-button__text {
        margin-left: 23px;
      }
    }

    &--divided {
      border-bottom: 1px solid #f0f0f0;
    }

    &--active {
      &.radio-button--bordered {
        -webkit-box-shadow: 0px 0px 0px 2px $colorRed inset;
        -moz-box-shadow: 0px 0px 0px 2px $colorRed inset;
        box-shadow: 0px 0px 0px 2px $colorRed inset;
      }
    }

    &__text {
      flex: 1;
      color: #4a4a4a;
      text-align: left;

      &--large {
        font-family: Roboto-Bold;
        font-size: 24px;
        line-height: 28px;
      }
    }
  }
}
</style>
